/* ====================== KEEP ALL ORIGINAL IMPORTS & STYLES ====================== */
import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useDispatch, useSelector } from "react-redux";

import { fetchNonAllocatedEmp } from "../../actions/NonAllocatedEmpAction";
import { fetchOTNonAllocatedLoc } from "../../actions/OTNonAllocationLocAction";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

/* -------------------- Styles (unchanged) -------------------- */
const PageContainer = styled(Box)(() => ({
  maxWidth: "900px",
  margin: "0 auto",
  padding: "10px 15px",
  fontFamily: "'Poppins', sans-serif",
}));

const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: "16px",
  boxShadow: "0px 6px 15px rgba(0,0,0,0.12)",
  border: "1px solid #d6dce5",
  backgroundColor: "#ffffff",
  maxHeight: "650px",
  overflowY: "auto",
  overflowX: "hidden",
  position: "relative",
  width: "100%",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  padding: "14px 10px",
  fontSize: "14px",
  textAlign: "center",
  "&.MuiTableCell-head": {
    backgroundColor: "#154c79",
    color: "#ffffff",
    fontWeight: 600,
    letterSpacing: "0.5px",
    borderBottom: "none",
    position: "sticky",
    top: 0,
    zIndex: 10,
    "&:first-of-type": {
      borderTopLeftRadius: "16px",
    },
    "&:last-of-type": {
      borderTopRightRadius: "16px",
    },
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "0.25s ease",
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(243, 246, 250, 0.6)",
  },
  "&:hover": {
    backgroundColor: "rgba(14, 60, 102, 0.09)",
    cursor: "pointer",
  },
}));

/* -------------------- Component -------------------- */
export default function NonAllocatedEmp() {
  const dispatch = useDispatch();

  const { loading: supLoading, data: supData } = useSelector(
    (state) => state.nonAllocatedEmp
  );
  const { loading: locLoading, data: locData } = useSelector(
    (state) => state.otNonAllocatedLoc
  );

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("supervisor");

  const formatDate = (dateObj) => {
    const d = new Date(dateObj);
    return d.toISOString().split("T")[0];
  };

  useEffect(() => {
    const formatted = formatDate(selectedDate);
    if (viewMode === "supervisor") {
      dispatch(fetchNonAllocatedEmp(formatted));
    } else {
      dispatch(fetchOTNonAllocatedLoc(formatted));
    }
  }, [dispatch, viewMode, selectedDate]);

  /* -------------------- DATA SOURCE -------------------- */
  const currentData = viewMode === "supervisor" ? supData : locData;
  const loading = viewMode === "supervisor" ? supLoading : locLoading;

  /* -------------------- LOCATION FILTER -------------------- */
  const excludedLocations = ["cad", "cac", "hao", "iim", "ism", "it"];
  const filteredData =
    viewMode === "location"
      ? currentData?.filter((item) => {
          const loc = (item.OTNon_location || item.Location || "")
            .toLowerCase()
            .trim();
          return !excludedLocations.includes(loc);
        })
      : currentData;

  /* -------------------- TOTAL COUNT -------------------- */
  const totalCount = filteredData?.reduce((sum, item) => {
    const countKey =
      viewMode === "supervisor" ? "Non_EmpCount" : "OTNon_EmpCount";
    return sum + Number(item[countKey] || 0);
  }, 0);

  const getCellValue = (item, key) => item[key] || "";

  /* -------------------- UI (UNCHANGED) -------------------- */
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageContainer>
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 700,
            color: "#0E3C66",
            textAlign: "center",
            mb: 2,
            letterSpacing: "1px",
          }}
        >
          Non-Allocated Employees
        </Typography>

        <Grid container spacing={2} sx={{ marginBottom: 2 }}>
          <Grid item xs={6}>
            <Button
              variant="contained"
              startIcon={<CalendarTodayIcon />}
              onClick={() => setOpenDatePicker(true)}
              sx={{
                width: "100%",
                height: "56px",
                borderRadius: "20px",
                backgroundColor: "#142c54",
                color: "#fff",
                fontWeight: "bold",
                textTransform: "none",
                fontSize: "16px",
                "&:hover": { backgroundColor: "#0D47A1" },
              }}
            >
              {formatDate(selectedDate)}
            </Button>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel
                sx={{
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  color: "#154C79",
                }}
              >
                View Type
              </InputLabel>
              <Select
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
                label="View Type"
                sx={{
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  borderRadius: "20px",
                  backgroundColor: "#F0F4F8",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  "& .MuiSelect-icon": {
                    color: "#154C79",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#154C79",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 250,
                      overflowY: "auto",
                    },
                  },
                }}
              >
                <MenuItem value="supervisor">Based on Supervisor</MenuItem>
                <MenuItem value="location">Based on Location</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {openDatePicker && (
          <DatePicker
            open={openDatePicker}
            value={selectedDate}
            onChange={(date) => {
              setSelectedDate(date);
              setOpenDatePicker(false);
            }}
            onClose={() => setOpenDatePicker(false)}
            renderInput={(params) => <TextField {...params} sx={{ display: "none" }} />}
          />
        )}

        {!loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Box
              sx={{
                background: "#e8f0fa",
                border: "1px solid #bbd0ea",
                padding: "8px 14px",
                borderRadius: "8px",
                textAlign: "center",
                fontWeight: 600,
                color: "#0E3C66",
              }}
            >
              Total Employees: {totalCount}
            </Box>
          </Box>
        )}

        <StyledTableContainer component={Paper}>
          <Table sx={{ minWidth: "auto", width: "100%", tableLayout: "auto" }}>
            <TableHead>
              <TableRow>
                {viewMode === "supervisor" ? (
                  <>
                    <StyledTableCell sx={{ textAlign: "center", width: "30%" }}>
                      Service No
                    </StyledTableCell>
                    <StyledTableCell sx={{ textAlign: "left", width: "45%" }}>
                      Supervisor Name
                    </StyledTableCell>
                  </>
                ) : (
                  <StyledTableCell sx={{ textAlign: "center", width: "50%" }}>
                    Location
                  </StyledTableCell>
                )}
                <StyledTableCell sx={{ textAlign: "center", width: viewMode === "supervisor" ? "25%" : "50%" }}>
                  Employee Count
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading &&
                filteredData?.map((item, index) => (
                  <StyledTableRow key={index}>
                    {viewMode === "supervisor" ? (
                      <>
                        <StyledTableCell sx={{ textAlign: "center" }}>
                          {getCellValue(item, "Non_serviceNo") || getCellValue(item, "ServiceNo")}
                        </StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "left" }}>
                          {getCellValue(item, "Non_SupName") || getCellValue(item, "SupervisorName")}
                        </StyledTableCell>
                      </>
                    ) : (
                      <StyledTableCell sx={{ textAlign: "center" }}>
                        {getCellValue(item, "OTNon_location") || getCellValue(item, "Location")}
                      </StyledTableCell>
                    )}
                    <StyledTableCell sx={{ textAlign: "center" }}>
                      {viewMode === "supervisor"
                        ? getCellValue(item, "Non_EmpCount") || getCellValue(item, "EmployeeCount")
                        : getCellValue(item, "OTNon_EmpCount") || getCellValue(item, "EmployeeCount")}
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              {loading && (
                <TableRow>
                  <TableCell colSpan={viewMode === "location" ? 2 : 3} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </PageContainer>
    </LocalizationProvider>
  );
}
